/**
 * ENG-8574: the tool-call scan cursor, and the coverage watermark derived from
 * it.
 *
 * THE CURSOR IS THE SAFETY PROPERTY. `POST /host/tool-calls` returns a status
 * code that is a contract, not decoration:
 *
 *   200 entitled:true   durably written (or already present) → ADVANCE
 *   200 entitled:false  org will never accept these rows     → ADVANCE
 *   400                 producer bug, retrying is futile     → ADVANCE past it
 *   404                 host not bound to this agent         → HOLD, stop
 *   500                 write failed                         → HOLD, retry
 *
 * Advancing on a 500 moves past lines that were never stored, and the transcript
 * they came from is gone in 7 days locally (30 by Claude Code's own
 * cleanupPeriodDays). That is a permanent, invisible hole in a compliance
 * record. This is the ENG-8337 / ENG-8346 cursor-advance strand; the whole point
 * of this module is not to re-create it.
 *
 * The asymmetry between the two "we ingested nothing" answers is the subtle
 * part: a not-entitled 200 must ADVANCE (holding retry-loops forever against an
 * org that will never accept the data) while a 500 must HOLD. Conflating them
 * either loses data or spins.
 *
 * WHY IT REWINDS TO THE EARLIEST UNPAIRED CALL. Ingest upserts with
 * `ignoreDuplicates: true`, so a row is written ONCE and can never be corrected.
 * A call whose `tool_result` had not been written when the scan passed ingests
 * with `is_error: null`, and a later re-scan that pairs it is a no-op — the NULL
 * is permanent. So the cursor stops at the first still-unpaired call rather than
 * at EOF, giving the result a chance to land before the line is passed. Paired
 * calls AFTER that point are still sent (sending is idempotent); only the
 * resume point is held back.
 *
 * That would wedge on a call that never gets a result — an agent killed
 * mid-turn — so the hold expires (UNPAIRED_GRACE_MS) and the cursor moves on,
 * accepting the permanent NULL as the honest answer for a call whose outcome
 * genuinely was never recorded.
 */

import { existsSync, readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

import { agentRuntimeKey } from './agent-runtime-key.js';
import { atomicWriteFileSync } from './atomic-write.js';

/**
 * MUST stay in lockstep with the CHECK constraint in migration 20260812000002
 * and with VALID_COVERAGE_DISPOSITIONS in
 * packages/api/src/routes/host-runtime.ts. Three copies in three languages; the
 * alternative is letting the constraint be the only guard, which turns one bad
 * value into a batch-wide 500 and, under the cursor contract above, a payload
 * the host re-offers forever.
 */
export const COVERAGE_DISPOSITIONS = [
  'ok',
  'not_entitled',
  'ingest_failed',
  'batch_rejected',
  'agent_unbound',
  'transcript_missing',
] as const;

export type CoverageDisposition = (typeof COVERAGE_DISPOSITIONS)[number];

/** What the ingest response means for the cursor. */
export type IngestOutcome =
  | { kind: 'ingested' }
  | { kind: 'not_entitled' }
  | { kind: 'batch_rejected'; reason: string }
  | { kind: 'agent_unbound' }
  | { kind: 'ingest_failed'; reason: string };

/**
 * Whether this outcome permits the cursor to move past the batch it describes.
 *
 * Written as a total function over the union — not an `if (ok)` at the call
 * site — so adding a new outcome is a compile error here rather than a silent
 * default. The default a call site would pick is "advance", which is the
 * data-losing direction.
 */
export function mayAdvance(outcome: IngestOutcome): boolean {
  switch (outcome.kind) {
    case 'ingested':
      return true;
    // Definitive: nothing will EVER be collected for this org. Holding would
    // re-offer the same lines forever against an answer that cannot change.
    case 'not_entitled':
      return true;
    // A producer bug. Retrying is futile and holding wedges every later call
    // behind it, so step over — loudly, and counted.
    case 'batch_rejected':
      return true;
    case 'agent_unbound':
      return false;
    case 'ingest_failed':
      return false;
  }
}

/** The disposition to record for this outcome. */
export function dispositionFor(outcome: IngestOutcome): CoverageDisposition {
  switch (outcome.kind) {
    case 'ingested':
      return 'ok';
    case 'not_entitled':
      return 'not_entitled';
    case 'batch_rejected':
      return 'batch_rejected';
    case 'agent_unbound':
      return 'agent_unbound';
    case 'ingest_failed':
      return 'ingest_failed';
  }
}

/**
 * Per-transcript-file scan state.
 *
 * TWO POSITIONS, NOT ONE (ENG-8740). Until this split, `byteOffset`/`lineOffset`
 * served as both the RESUME POINT and the COMPLETED-COVERAGE WATERMARK, and the
 * coverage row was projected straight off them. Those two coincide only when a
 * window was read in full and every call in it was accepted. Three routine
 * events move the resume point without covering anything:
 *
 *   * the first-run seek to EOF — the cursor jumps to the end having scanned
 *     nothing, and the row then claimed every byte below it,
 *   * the unpaired rewind — the resume point goes BACKWARDS to a call whose
 *     `tool_result` has not landed, over ground that really was covered,
 *   * the post-rotation rewind to 0 — a different file entirely under the same
 *     key.
 *
 * With one pair of numbers, the first overstates and the second understates, and
 * the table's forward-only clamp then makes the overstatement PERMANENT. Two
 * pairs let each move on its own terms: `scannedThrough*` only ever counts ground
 * read and accounted for, `byteOffset`/`lineOffset` go wherever the next read
 * must start.
 */
export interface FileCursor {
  /** Resume point. Byte offset of the first line NOT yet accounted for. */
  byteOffset: number;
  /** Line ordinal at `byteOffset`, so `line_offset` stays absolute. */
  lineOffset: number;
  /**
   * COMPLETED-COVERAGE WATERMARK: the high mark of ground actually scanned AND
   * accounted for, never merely seeked past. Monotonic within a generation.
   */
  scannedThroughByte: number;
  /** Line ordinal companion to `scannedThroughByte`. */
  scannedThroughLine: number;
  /** Where the FIRST scan of this file began — 0, or the scan-from-now bound. */
  startedFromLine: number;
  /**
   * Byte companion to `startedFromLine`, and the field that makes a seek
   * legible.
   *
   * The first-run bound seeks by BYTE, so it can offer no line ordinal for the
   * skipped prefix — `startedFromLine` stays 0 while the cursor sits at EOF.
   * Without this, a coverage row said "through byte 45000, from line 0" and the
   * only available reading was that all 45000 bytes were covered. With it the
   * claim is the INTERVAL `[startedFromByte, scannedThroughByte]`, which on a
   * pure seek is empty — the honest statement of a scan that read nothing.
   */
  startedFromByte: number;
  /**
   * Bumped each time the extractor reports the file shrank below the cursor.
   *
   * A rotation replaces the file while `(agent_id, session_id, transcript_ref)`
   * stays the same, so the stored watermark is about a file that no longer
   * exists. The forward-only clamp cannot tell that from a host trying to
   * retract coverage, and keeps the dead file's high mark. This counter is what
   * distinguishes them: a bump means "new file, the previous claim was about
   * different bytes", and only then may the watermark legitimately go down.
   */
  rewindGeneration: number;
  /** The last window stopped on its byte budget with more file left to read. */
  lastWindowTruncated: boolean;
  /** Running count of calls extracted from this file. */
  callsExtracted: number;
  /** ISO time the earliest currently-unpaired call was first seen, else null. */
  unpairedSince: string | null;
  /** Last ingest disposition for this file. */
  lastDisposition: CoverageDisposition;
  /**
   * The run scope in effect at the end of the last window (ENG-8832), so a
   * resumed scan does not restart unattributed in the middle of a run.
   *
   * Persisted because the scanner reads a BOUNDED WINDOW per tick: a run marker
   * arrives once, at the head of its run, and a busy agent's calls land many
   * windows later. Without carrying the scope across ticks, the first window
   * after the marker would attribute correctly and every window after it would
   * report NULL — an under-attribution that looks exactly like "this work was
   * not part of a run".
   *
   * NULL means no marker has been seen yet on this file, which is the honest
   * state after a first-run seek (the prefix holding the marker was never read).
   */
  runScopeId: string | null;
  /** ISO time of the last scan attempt. */
  lastScanAt: string;
  /** ISO time of the first scan of this file. */
  firstScanAt: string;
  transcriptRelPath: string | null;
}

interface CursorFile {
  version: 1;
  /** `<sessionId>` NUL `<transcriptRef>` -> cursor. */
  files: Record<string, FileCursor>;
}

/**
 * How long to hold the cursor at an unpaired call before giving up on its
 * result and accepting a permanent `is_error: null`.
 *
 * Long enough to cover a genuinely slow tool (a multi-minute build, a long
 * Bash) — the cost of waiting is only that the call is reported late. Short
 * enough that an agent killed mid-turn does not stall its own audit coverage
 * indefinitely, which would look exactly like "the scanner stopped working".
 */
export const UNPAIRED_GRACE_MS = 30 * 60 * 1000;

/**
 * Separator between the two halves of a cursor key.
 *
 * Written as an ESCAPE, never as a raw byte: a literal NUL in a source file
 * makes git treat the whole file as binary, so it stops appearing in diffs
 * entirely and no reviewer ever reads it again.
 *
 * NUL itself because both halves are filesystem-derived — `transcriptRef` comes
 * from a filename — and a separator either half could contain would let one
 * file's cursor be addressed as another's. The symptom would be a silently
 * wrong resume point, not an error.
 */
const KEY_SEP = '\u0000';

/** Key for one transcript FILE within a session. */
export function cursorKey(sessionId: string | null, transcriptRef: string): string {
  return `${sessionId ?? ''}${KEY_SEP}${transcriptRef}`;
}

/** Split a cursor key back into its two halves. */
export function parseCursorKey(key: string): { sessionId: string | null; transcriptRef: string } {
  const i = key.indexOf(KEY_SEP);
  if (i < 0) return { sessionId: null, transcriptRef: key };
  const sessionId = key.slice(0, i);
  return { sessionId: sessionId.length > 0 ? sessionId : null, transcriptRef: key.slice(i + 1) };
}

/** Where an agent's cursor state lives. */
export function cursorStatePath(codeName: string, homeDir?: string): string {
  const home = homeDir ?? (process.env['HOME']?.trim() || homedir());
  // agentRuntimeKey returns the durable KEY (agent_id once the agent is on the
  // id-keyed layout, else the codeName) by resolving the ADR-0049 symlink
  // contract, so cursor state follows the agent through that migration instead
  // of being stranded under an old name. The join below is therefore keyed on
  // the resolved value and never on the codename itself.
  const key = agentRuntimeKey(codeName, homeDir);
  return join(home, '.augmented', key, 'tool-call-cursors.json');
}

/**
 * Read persisted cursors. Missing or corrupt state reads as empty.
 *
 * Empty is the SAFE direction only because it is paired with the first-run
 * bound in the scanner: a fresh cursor starts from the current end of file, not
 * from byte zero, so losing this file costs a gap in coverage rather than a
 * re-scan of every transcript on the host. The gap is recorded — the coverage
 * row's started_from_line says where the new scan began.
 */
export function loadCursors(path: string): Map<string, FileCursor> {
  const out = new Map<string, FileCursor>();
  if (!existsSync(path)) return out;
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8')) as CursorFile;
    if (!parsed || parsed.version !== 1 || typeof parsed.files !== 'object') return out;
    for (const [k, v] of Object.entries(parsed.files)) {
      if (!v || typeof v !== 'object') continue;
      // Type alone is not enough. A NEGATIVE byteOffset makes readSync throw
      // inside the extractor's swallowed catch, so the file is never scanned
      // again and nothing reports it; an out-of-range line ordinal is rejected
      // by the coverage route's own validation. Drop the entry instead — a lost
      // cursor costs a recorded gap, a poisoned one costs silence. (CodeRabbit.)
      const offsetsOk = (
        [
          'byteOffset',
          'lineOffset',
          'startedFromLine',
          'startedFromByte',
          'scannedThroughByte',
          'scannedThroughLine',
          'rewindGeneration',
          'callsExtracted',
        ] as const
      ).every((f) => {
        const n = v[f];
        return n === undefined || (typeof n === 'number' && Number.isSafeInteger(n) && n >= 0);
      });
      if (typeof v.byteOffset !== 'number' || typeof v.lineOffset !== 'number' || !offsetsOk) continue;
      // A cursor written by a PRE-ENG-8740 host has neither watermark nor
      // startedFromByte. Defaulting the watermark to the resume point would
      // re-import the exact overstatement this split exists to remove — for a
      // file that was first seen under the old code, byteOffset IS the seek
      // point. Defaulting BOTH to the resume point instead makes the interval
      // empty, so the upgrade tick claims nothing it cannot support and the
      // watermark grows from there.
      const startedFromByte =
        typeof v.startedFromByte === 'number' ? v.startedFromByte : v.byteOffset;
      out.set(k, {
        byteOffset: v.byteOffset,
        lineOffset: v.lineOffset,
        startedFromLine: typeof v.startedFromLine === 'number' ? v.startedFromLine : 0,
        startedFromByte,
        scannedThroughByte:
          typeof v.scannedThroughByte === 'number' ? v.scannedThroughByte : startedFromByte,
        scannedThroughLine:
          typeof v.scannedThroughLine === 'number'
            ? v.scannedThroughLine
            : typeof v.startedFromLine === 'number'
              ? v.startedFromLine
              : 0,
        rewindGeneration: typeof v.rewindGeneration === 'number' ? v.rewindGeneration : 0,
        lastWindowTruncated: v.lastWindowTruncated === true,
        callsExtracted: typeof v.callsExtracted === 'number' ? v.callsExtracted : 0,
        // A pre-ENG-8832 cursor has no scope. Null is the correct upgrade value
        // and not merely the convenient one: the host genuinely does not know
        // which run was in scope, and inventing one would put a fabricated
        // attribution on the first rows written after every host upgrade.
        runScopeId: typeof v.runScopeId === 'string' && v.runScopeId ? v.runScopeId : null,
        unpairedSince: typeof v.unpairedSince === 'string' ? v.unpairedSince : null,
        lastDisposition: COVERAGE_DISPOSITIONS.includes(v.lastDisposition as CoverageDisposition)
          ? (v.lastDisposition as CoverageDisposition)
          : 'ok',
        lastScanAt: typeof v.lastScanAt === 'string' ? v.lastScanAt : new Date(0).toISOString(),
        firstScanAt: typeof v.firstScanAt === 'string' ? v.firstScanAt : new Date(0).toISOString(),
        transcriptRelPath: typeof v.transcriptRelPath === 'string' ? v.transcriptRelPath : null,
      });
    }
  } catch {
    // Corrupt state is not worth failing a scan over; see the note above on why
    // empty is survivable.
  }
  return out;
}

/**
 * Persist cursors atomically.
 *
 * Atomic because the manager can be killed at any moment (self-update, exit 75,
 * a restart-breaker trip). A torn cursor file would read back as empty on the
 * next boot, and with the first-run bound that silently skips everything written
 * in between — the one failure this module exists to prevent, arriving by the
 * back door.
 */
export function saveCursors(path: string, cursors: Map<string, FileCursor>): void {
  const files: Record<string, FileCursor> = {};
  for (const [k, v] of cursors) files[k] = v;
  atomicWriteFileSync(path, JSON.stringify({ version: 1, files } satisfies CursorFile, null, 2));
}

/**
 * Decide the next resume point for a scanned file.
 *
 * Split out from the scanner and made pure so the cursor contract can be tested
 * without a filesystem, an HTTP client or a clock — the ENG-8337 failure was in
 * exactly this decision, reached through code that was awkward to test directly.
 */
export function nextCursor(args: {
  previous: FileCursor;
  /** Where the window stopped (past the last complete line). */
  endByte: number;
  endLine: number;
  /** Earliest still-unpaired call in the window, if any. */
  earliestUnpaired: { byteStart: number; lineOffset: number; runScopeId?: string | null } | null;
  outcome: IngestOutcome;
  callsExtracted: number;
  nowMs: number;
  /** Run scope at `endByte` (ENG-8832), from the window the extractor just read. */
  endRunScopeId?: string | null;
  /** The extractor found the file shorter than the cursor and restarted at 0. */
  rewound?: boolean;
  /** The window stopped on its byte budget with more file left (ENG-8740). */
  truncated?: boolean;
}): FileCursor {
  const { endByte, endLine, earliestUnpaired, outcome, nowMs } = args;
  const nowIso = new Date(nowMs).toISOString();
  const disposition = dispositionFor(outcome);
  const truncated = args.truncated === true;

  // A ROTATION IS A NEW FILE, AND EVERYTHING ABOUT THE OLD ONE STOPS APPLYING.
  //
  // The extractor has already restarted this window at byte 0 (see its `rewound`
  // branch), so the offsets below describe a file the stored state has never
  // seen. Carrying that state forward is wrong three separate ways, and until
  // ENG-8740 all three were live because `rewound` reached nothing but a log
  // line:
  //
  //  * the watermark would keep the DEAD file's high mark, and the table's
  //    forward-only clamp would make that permanent — a coverage claim over
  //    bytes that no longer exist;
  //  * `startedFrom*` would still describe the old file's first scan;
  //  * worst, the unpaired branch below clamps the resume point with
  //    `Math.max(previous.byteOffset, …)`, which would pin it PAST the end of
  //    the shorter new file. The next tick then rewinds again, and the file is
  //    re-read from 0 forever while never advancing — a silent, permanent spin.
  //
  // The generation bump is what earns the reset downstream: it is the one signal
  // that lets the DB trigger tell a legitimate retraction from a host trying to
  // erase coverage it really had.
  const previous: FileCursor = args.rewound
    ? {
        ...args.previous,
        byteOffset: 0,
        lineOffset: 0,
        scannedThroughByte: 0,
        scannedThroughLine: 0,
        startedFromByte: 0,
        startedFromLine: 0,
        rewindGeneration: args.previous.rewindGeneration + 1,
        // The call this was waiting on lived in the old file. Holding its clock
        // would apply an unrelated deadline to the new one.
        unpairedSince: null,
        // Same reasoning for the run scope (ENG-8832): the marker that opened it
        // was in a file that no longer exists. Re-derive from the new file's own
        // markers rather than stamping the head of a fresh transcript with a run
        // that ended somewhere unreadable. (The extractor already dropped it on
        // its side; this keeps the two from disagreeing on a HOLD, where the
        // window's value is discarded.)
        runScopeId: null,
      }
    : args.previous;

  // HOLD. Not one byte moves, and the unpaired clock does not start either —
  // the batch was never accepted, so nothing about it is settled. (A rewind
  // reset above still stands: it is a fact about the FILE, not about the batch,
  // and re-reading a rotated file from 0 is the only correct resume point
  // whatever ingest said.)
  if (!mayAdvance(outcome)) {
    return {
      ...previous,
      lastDisposition: disposition,
      lastScanAt: nowIso,
      lastWindowTruncated: truncated,
    };
  }

  // The batch landed, so the calls in it are accounted for.
  const callsExtracted = previous.callsExtracted + args.callsExtracted;

  /**
   * Advance the watermark over ground that was scanned AND accepted.
   *
   * `Math.max` against the previous value, not a bare assignment: the resume
   * point legitimately moves backwards to an unpaired call, and the watermark
   * must not follow it down — that ground was covered and its calls were sent.
   * Within a generation this is the whole monotonicity guarantee; the table's
   * forward-only trigger is then a backstop against a wiped host, rather than
   * the mechanism that makes the number correct.
   */
  const covered = (throughByte: number, throughLine: number) => ({
    scannedThroughByte: Math.max(previous.scannedThroughByte, throughByte),
    scannedThroughLine: Math.max(previous.scannedThroughLine, throughLine),
  });

  if (!earliestUnpaired) {
    return {
      ...previous,
      byteOffset: endByte,
      lineOffset: endLine,
      ...covered(endByte, endLine),
      callsExtracted,
      unpairedSince: null,
      runScopeId: args.endRunScopeId ?? null,
      lastDisposition: disposition,
      lastScanAt: nowIso,
      lastWindowTruncated: truncated,
    };
  }

  // Something is unpaired. Start (or continue) the grace clock.
  const since = previous.unpairedSince ?? nowIso;
  const waited = nowMs - Date.parse(since);
  // `!isFinite ||`, not `isFinite &&`: an unparseable persisted timestamp would
  // otherwise hold the cursor at this call forever, which is the same permanent
  // silent stall the grace period exists to prevent. Give up instead.
  // (CodeRabbit.)
  if (!Number.isFinite(waited) || waited >= UNPAIRED_GRACE_MS) {
    // Gave it long enough. The result is never coming — an agent killed
    // mid-turn, or a tool that never returned. Move past, and accept the NULL
    // as the honest record of a call whose outcome was never written.
    return {
      ...previous,
      byteOffset: endByte,
      lineOffset: endLine,
      ...covered(endByte, endLine),
      callsExtracted,
      unpairedSince: null,
      runScopeId: args.endRunScopeId ?? null,
      lastDisposition: disposition,
      lastScanAt: nowIso,
      lastWindowTruncated: truncated,
    };
  }

  // Rewind the resume point to the unpaired call so the next scan can pair it.
  // Never FORWARD of where we already are: a window that began before an
  // unpaired call from an earlier window must not drag the cursor backwards
  // into lines already accounted for.
  const resumeByte = Math.max(previous.byteOffset, Math.min(endByte, earliestUnpaired.byteStart));
  const resumeLine = Math.max(previous.lineOffset, Math.min(endLine, earliestUnpaired.lineOffset));
  return {
    ...previous,
    byteOffset: resumeByte,
    lineOffset: resumeLine,
    // Covered ground stops at the unpaired call: the calls from there on were
    // deliberately NOT sent (their `is_error` would be written null and
    // `ignoreDuplicates` makes that permanent), so claiming them would be
    // claiming rows the control plane has never been offered.
    ...covered(resumeByte, resumeLine),
    callsExtracted,
    unpairedSince: since,
    // The scope AT THE UNPAIRED CALL, not at the end of the window (ENG-8832).
    // This is the one branch where the two can differ and it matters: the
    // unpaired call was deliberately not sent, so no stored row protects it, and
    // a marker between it and the window's end would otherwise file it under the
    // NEXT run when the re-read attributes it.
    runScopeId: earliestUnpaired.runScopeId ?? null,
    lastDisposition: disposition,
    lastScanAt: nowIso,
    lastWindowTruncated: truncated,
  };
}

/**
 * A fresh cursor for a file never scanned before.
 *
 * The watermark starts AT the start point, not at zero: the covered interval
 * `[startedFromByte, scannedThroughByte]` is empty, which is the exact claim a
 * file that has been seeked but not scanned can support. Starting the watermark
 * at 0 instead would make the interval `[startByte, 0]` — negative, and
 * meaningless to any reader.
 */
export function initialCursor(args: {
  startByte: number;
  startLine: number;
  transcriptRelPath: string | null;
  nowMs: number;
}): FileCursor {
  const iso = new Date(args.nowMs).toISOString();
  return {
    byteOffset: args.startByte,
    lineOffset: args.startLine,
    scannedThroughByte: args.startByte,
    scannedThroughLine: args.startLine,
    startedFromLine: args.startLine,
    startedFromByte: args.startByte,
    rewindGeneration: 0,
    lastWindowTruncated: false,
    callsExtracted: 0,
    // No marker has been seen (ENG-8832). On the scan-from-now path the prefix
    // that held one was never read, so "not determined" is the literal truth.
    runScopeId: null,
    unpairedSince: null,
    lastDisposition: 'ok',
    lastScanAt: iso,
    firstScanAt: iso,
    transcriptRelPath: args.transcriptRelPath,
  };
}

/**
 * The wire shape of one coverage row.
 *
 * `through_*` and `started_from_*` are ONE CLAIM, not two facts: the row asserts
 * coverage of the interval between them, and either endpoint read alone is
 * meaningless. A first sighting reports them equal — an empty interval, i.e.
 * "nothing scanned yet" — which is why a byte companion to `started_from_line`
 * had to exist before the seek could be stated honestly at all.
 */
export interface CoverageRow {
  session_id: string | null;
  transcript_ref: string;
  transcript_rel_path: string | null;
  through_line: number;
  through_byte: number;
  started_from_line: number;
  started_from_byte: number;
  /** Resume point. May sit BEHIND the watermark while a result is awaited. */
  scan_position_byte: number;
  scan_position_line: number;
  /** More file remains beyond the watermark; the scan is mid-backlog. */
  scan_window_truncated: boolean;
  /** Rotation counter. A bump authorises the watermark to move DOWN. */
  rewind_generation: number;
  calls_extracted: number;
  last_disposition: CoverageDisposition;
  extractor_version: string;
  redaction_rule_version: string;
}

/**
 * Project a cursor into the coverage row that reports it.
 *
 * `through_*` comes from the WATERMARK, never from the resume point (ENG-8740).
 * The resume point still travels, as `scan_position_*`, where it describes
 * progress rather than proof.
 *
 * WHICH MECHANISM FIXES WHICH HALF — worth stating, because the two are easy to
 * credit to each other. The original defect had two faces:
 *
 *   * the SEEK. A first sighting reported `through_byte` = file size with
 *     `through_line` = 0. What fixes that is `started_from_byte` EXISTING, so
 *     the claim can be read as an interval and comes out empty. It is not fixed
 *     by this projection: on a pure seek the resume point and the watermark are
 *     the same number, and swapping one for the other changes nothing.
 *   * the UNPAIRED HOLD. The resume point rewinds backwards over ground that was
 *     genuinely covered, and projecting it understated the watermark — leaving
 *     the table's forward-only clamp to repair the number the host got wrong.
 *     THAT is what this projection fixes, and it is why the host is now correct
 *     on its own rather than relying on a trigger to correct it.
 *
 * Verified by sabotage, not by reading: reverting this projection to
 * `cursor.byteOffset` fails exactly one test — the unpaired case — and leaves
 * every seek assertion green.
 */
export function coverageRowFor(
  key: string,
  cursor: FileCursor,
  versions: { extractor: string; redaction: string },
): CoverageRow {
  const { sessionId, transcriptRef } = parseCursorKey(key);
  return {
    session_id: sessionId,
    transcript_ref: transcriptRef,
    transcript_rel_path: cursor.transcriptRelPath,
    through_line: cursor.scannedThroughLine,
    through_byte: cursor.scannedThroughByte,
    started_from_line: cursor.startedFromLine,
    started_from_byte: cursor.startedFromByte,
    scan_position_byte: cursor.byteOffset,
    scan_position_line: cursor.lineOffset,
    scan_window_truncated: cursor.lastWindowTruncated,
    rewind_generation: cursor.rewindGeneration,
    calls_extracted: cursor.callsExtracted,
    last_disposition: cursor.lastDisposition,
    extractor_version: versions.extractor,
    redaction_rule_version: versions.redaction,
  };
}
